import React, { useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Label,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { LogD } from '../../../function/LogLapper';
import {
  MeasureInfoType,
  GetMeasureInfoType,
  SelectDataType,
  GetGraphLineData,
  LineInfo,
  RawWaveComparisonData,
} from './RawWaveformComparison';
import dayjs from 'dayjs';
import { Minimize } from '@material-ui/icons';
import * as Constant from '../../../Constant';
import { ToValueRoundInternal, ToPowIndex, ToValueMaxRound, ToValueMinRound } from '../../../function/CalculationModel';
import { useTranslation } from 'react-i18next';
import { GetMemoryScaleByNumber } from '../../../function/ChartCommon';
const useStyles = makeStyles(
  createStyles({
    customTooltip: {
      background: '#FFFFEE',
      fontSize: '14px',
      padding: '10px 10px 0px 20px',
    },
  })
);

export type GraphDispSetting = {
  y1Min: number;
  y1Max: number;
  y2Min: number;
  y2Max: number;
  xMin: number;
  xMax: number;
  isPileup: boolean;
  isDCRemoval: boolean;
};

type GraphData = {
  lineNo: number;
  lineName: string;
  points: GraphPoint[];
  valueUpper: number;
  valueUpperLimit: number;
  selectFlg: boolean;
  infoColor: string;
};
type GraphPoint = {
  xValue: number;
  yValue: number;
};

type GraphSetting = {
  upper: number;
  upperLimit: number;
  y1Min: number;
  y1Max: number;
  y2Min: number;
  y2Max: number;
  xMin: number;
  xMax: number;
  backColor: string;
  memoryFontColor: string;
  yUnit1: string;
  yUnit2: string;
  xUnit: string;
  yUnitColor: string;
  xUnitColor: string;
  gridColor: string;
};

type Props = {
  line1: SelectDataType;
  line2: SelectDataType;
  isLineShows: boolean[];
  mesList: MeasureInfoType[];
  update: string;
  graphDispSetting: GraphDispSetting;
  onLoading: (isFlg: boolean) => void;
  onChagneYMinMax: (min1: number, max1: number, min2: number, max2: number) => void;
  chartContainerStyle: string;
  isAutoRangeRef: boolean;
  onAutoRangeSet: (data: boolean) => void;
};

const aryMax = function (a: number, b: number): number {
  return Math.max(a, b);
};
const aryMin = function (a: number, b: number): number {
  return Math.min(a, b);
};
/**
 * グラフスケールメモリ配列を返す
 * @param min
 * @param max
 * @returns
 */
const getTicks = (min: number, max: number): number[] => {
  if (min < 0 && max < 0) {
    return [min, max];
  }
  if (min > 0 && max > 0) {
    return [min, max];
  }
  return [min, 0, max];
};

/**
 * Y軸の値の最大値を取得
 * @param waveData
 * @returns
 */
const getLineInfoYMax = (waveData: GraphData): number => {
  let max = 0;

  if (waveData && waveData.points.length !== 0) {
    max = waveData.points.map((r) => r.yValue).reduce(aryMax);
  }
  if (max === 0) {
    return 1;
  } else {
    return max;
  }
  return 1;
};

/**
 * Y軸の値の最小値を取得
 * @param waveData
 * @returns
 */
const getLineInfoYMin = (waveData: GraphData): number => {
  let min = 0;

  if (waveData && waveData.points.length !== 0) {
    min = waveData.points.map((r) => r.yValue).reduce(aryMin);
  }
  if (min === 0) {
    return 0;
  } else {
    return min;
  }
  return 0;
};

/**
 * 二つの数字を受け取って、絶対値で比較して大きい値を返す関数
 * @param number1
 * @param number2
 * @returns
 */
const getMaxAbsoluteValue = (number1: number, number2: number): number => {
  const absolute1 = Math.abs(number1);
  const absolute2 = Math.abs(number2);

  if (absolute1 > absolute2) {
    return absolute1;
  } else if (absolute2 > absolute1) {
    return absolute2;
  } else {
    return absolute1;
  }
};
const createChartPoints = (
  chartDt: LineInfo,
  pointCount: number,
  startIndex: number,
  xOffset: number,
  isDCRemoval: boolean
): Array<GraphPoint> => {
  console.debug(`chartDt=${chartDt} pointCount=${pointCount} startIndex=${startIndex} xOffset=${xOffset}`);
  var points = new Array<GraphPoint>();
  var sIdex = startIndex === -1 ? chartDt.XaxisStartIndex : startIndex;

  chartDt.XValues.filter((tmp) => tmp > sIdex + xOffset).forEach((item, index) => {
    if (pointCount > index) {
      points.push({
        xValue: (item - sIdex) * chartDt.SamplingRate,
        yValue: isDCRemoval ? chartDt.YValues[item] - chartDt.YaxisOffset : chartDt.YValues[item],
      });
    }
  });
  console.debug(['★points', points]);
  return points;
};
const GetChartPointCount = (min: number, max: number, samplingRate: number): number => {
  console.debug(`(${max} - ${min}) / ${samplingRate}`);
  return (max - min) / samplingRate;
};

const UpDateGraphData = (
  data: RawWaveComparisonData | undefined,
  xmin: number,
  xmax: number,
  isPileup: boolean,
  isDCRemoval: boolean
): GraphData[] => {
  console.debug(['UpDateGraphData data , isPileup,isDCRemoval', data, isPileup, isDCRemoval]);

  var ret: GraphData[] = [];

  if (data?.OneLineData != undefined) {
    var item: GraphData = {
      lineNo: 1,
      valueUpper: 0,
      valueUpperLimit: 0,
      selectFlg: true,
      points: [],
      infoColor: 'red',
      lineName: '1',
    };

    item.lineName = data.OneLineData.ItemName;
    var rowCount = GetChartPointCount(xmin, xmax, data.OneLineData.SamplingRate);

    var startIndex = data.OneLineData.XaxisStartIndex;
    var xoffset = xmin / data.OneLineData.SamplingRate;

    var points = createChartPoints(data.OneLineData, rowCount, startIndex, xoffset, isDCRemoval);
    item.points = points;
    ret.push(item);
    console.debug(['★item', item]);
  }

  if (data?.TowLineData != undefined) {
    var item: GraphData = {
      lineNo: 2,
      valueUpper: 0,
      valueUpperLimit: 0,
      selectFlg: true,
      points: [],
      infoColor: 'blue',
      lineName: '2',
    };
    item.lineName = data.TowLineData.ItemName;
    var rowCount = GetChartPointCount(xmin, xmax, data.TowLineData.SamplingRate);

    var startIndex = 0;
    if (data?.OneLineData == null) {
      startIndex = data.TowLineData.XaxisStartIndex;
    } else {
      startIndex = isPileup === true ? data.TowLineData.XaxisStartIndex : data.OneLineData.XaxisStartIndex;
    }

    var xoffset = xmin / data.TowLineData.SamplingRate;

    var points = createChartPoints(data.TowLineData, rowCount, startIndex, xoffset, isDCRemoval);
    item.points = points;
    ret.push(item);
    console.debug(item);
  }
  console.debug(['★ret', ret]);
  return ret;
};

//対象レコード取得
const GetTargetData = (mesList: MeasureInfoType[], selectDt: SelectDataType): MeasureInfoType | undefined => {
  var mes = mesList.find((item) => item.id == selectDt.mesid);
  var dev = mesList.find((item) => item.id == selectDt.devid);
  var itemdt = mesList.find((item) => item.id == selectDt.itemid);

  if (mes && dev && itemdt) {
    var target = mesList.find((item) => {
      return item.KindNo === mes?.KindNo && item.DevIndex === dev?.DevIndex && item.ItemNameNo === itemdt?.ItemNameNo;
    });

    return target;
  }
  return undefined;
};

const RawWaveformComparisonChart: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [graphData, setGraphData] = useState<GraphData[]>([]);
  const [fetchData, setFetchData] = useState<RawWaveComparisonData>();

  const [graphSetting, setGraphSetting] = useState<GraphSetting>({
    upper: 0,
    upperLimit: 0,
    y1Max: 0,
    y1Min: 0,
    y2Max: 0,
    y2Min: 0,
    xMax: 0,
    xMin: 0,
    backColor: Constant.GraphThemeLight.BACK_COLOR,
    memoryFontColor: Constant.GraphThemeLight.MEMORY_FONT_COLOR,
    yUnit1: '',
    yUnit2: '',
    xUnit: '',
    yUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    xUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    gridColor: Constant.GraphThemeLight.GRID_COLOR,
  });

  const isLineShow = (index: number): boolean => {
    console.debug(['isLineShow -  index,isLineShows', index, props.isLineShows]);
    if (props.isLineShows.length > index) {
      return props.isLineShows[index];
    }
    return true;
  };
  //表示ボタン押下時
  useEffect(() => {
    console.debug(['mesList,line1Setting,line2Setting', props.mesList, props.line1, props.line2]);
    props.onLoading(true);
    var target1 = GetTargetData(props.mesList, props.line1);
    var day1 = dayjs(props.line1.selectDate).format('YYYY-MM-DD');
    var time1 = props.line1.selectTime;

    var target2 = GetTargetData(props.mesList, props.line2);
    var day2 = dayjs(props.line2.selectDate).format('YYYY-MM-DD');
    var time2 = props.line2.selectTime;
    GetGraphLineData(
      Number(target1?.PlcNo),
      Number(target1?.AiCard),
      Number(target1?.AiCh),
      `${day1} ${time1}`,
      Number(target2?.PlcNo),
      Number(target2?.AiCard),
      Number(target2?.AiCh),
      `${day2} ${time2}`
    ).then((ret) => {
      setFetchData(ret);
      props.onLoading(false);
    });
  }, [props.update]);

  //グラフデータ取得後処理
  useEffect(() => {
    var gdata = UpDateGraphData(
      fetchData,
      props.graphDispSetting.xMin,
      props.graphDispSetting.xMax,
      props.graphDispSetting.isPileup,
      props.graphDispSetting.isDCRemoval
    );

    if (fetchData?.OneLineData || fetchData?.TowLineData) {
      if (props.isAutoRangeRef === true) {
        props.onAutoRangeSet(false);
        //console.debug("★isAutoRange");
        var y1Max = getLineInfoYMax(gdata[0]);
        var y1Min = getLineInfoYMin(gdata[0]);
        //console.debug(["★★y1Max,y1Min", y1Max, y1Min]);

        var y2Max = getLineInfoYMax(gdata[1]);
        var y2Min = getLineInfoYMin(gdata[1]);
        //console.debug(["★★y2Max,y2Min", y2Max, y2Min]);

        //小数点切り捨て
        y1Max = Math.round(y1Max * 100) / 100;
        y1Min = Math.round(y1Min * 100) / 100;
        y2Max = Math.round(y2Max * 100) / 100;
        y2Min = Math.round(y2Min * 100) / 100;
        //console.debug(["★y1Max,y1Min,y2Max,y2Min", y1Max, y1Min, y2Max, y2Min]);
        y1Max = ToValueMaxRound(y1Max);
        y1Min = ToValueMinRound(y1Min);
        y2Max = ToValueMaxRound(y2Max);
        y2Min = ToValueMinRound(y2Min);
        //console.debug(["★★y1Max,y1Min,y2Max,y2Min", y1Max, y1Min, y2Max, y2Min]);

        const y1 = getMaxAbsoluteValue(y1Max, y1Min);
        y1Max = y1;
        y1Min = -y1;

        const y2 = getMaxAbsoluteValue(y2Max, y2Min);
        y2Max = y2;
        y2Min = -y2;

        var unit1 = fetchData?.OneLineData != null ? fetchData.OneLineData.UnitSymbol : '';
        var unit2 = fetchData?.TowLineData != null ? fetchData.TowLineData.UnitSymbol : '';
        //console.debug(["★unit1,unit2", unit1, unit2]);

        setGraphSetting({
          ...graphSetting,
          y1Max: y1Max,
          y1Min: y1Min,
          y2Max: y2Max,
          y2Min: y2Min,
          yUnit1: unit1,
          yUnit2: unit2,
          xUnit: 'msec',
        });
        console.debug(['fetchData', 'y1Max', 'y1Min', 'y2Max', 'y2Min', fetchData, y1Max, y1Min, y2Max, y2Min]);
        props.onChagneYMinMax(y1Min, y1Max, y2Min, y2Max);
      } else {
        var unit1 = fetchData?.OneLineData != null ? fetchData.OneLineData.UnitSymbol : '';
        var unit2 = fetchData?.TowLineData != null ? fetchData.TowLineData.UnitSymbol : '';

        setGraphSetting({
          ...graphSetting,
          yUnit1: unit1,
          yUnit2: unit2,
          xUnit: 'msec',
        });
      }
    }

    setGraphData(gdata);
  }, [fetchData]);

  //グラフデータ取得後処理
  useEffect(() => {
    var gdata = UpDateGraphData(
      fetchData,
      props.graphDispSetting.xMin,
      props.graphDispSetting.xMax,
      props.graphDispSetting.isPileup,
      props.graphDispSetting.isDCRemoval
    );

    if (fetchData) {
      var y1Max = props.graphDispSetting.y1Max;
      var y1Min = props.graphDispSetting.y1Min;
      var y2Max = props.graphDispSetting.y2Max;
      var y2Min = props.graphDispSetting.y2Min;

      setGraphSetting({ ...graphSetting, y1Max: y1Max, y1Min: y1Min, y2Max: y2Max, y2Min: y2Min });
      console.debug(['y1Max,y1Min,y2Max,y2Min', y1Max, y1Min, y2Max, y2Min]);
    }

    setGraphData(gdata);
  }, [props.graphDispSetting]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active) {
      return (
        <div className={classes.customTooltip}>
          <div className="label">{`${t('RawWaveformComparisonChart.時間', '時間')} : ${Number(label).toFixed(3)}`}</div>
          {payload?.map((r: any, index: any) => {
            if (index <= 2 && r.name !== 'yValue') {
              return (
                <div style={{ color: r.color }}>
                  {r.name} : {Number(r.value).toFixed(3)}
                </div>
              );
            }
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer className={props.chartContainerStyle} width="99%">
      <LineChart
        style={{ background: graphSetting.backColor }}
        width={500}
        height={300}
        margin={{ top: 10, right: 50, left: 1, bottom: 0 }}
      >
        <CartesianGrid stroke={graphSetting.gridColor} strokeDasharray="3 3" />
        <XAxis
          dataKey="xValue"
          type="number"
          allowDuplicatedCategory={false}
          stroke={graphSetting.memoryFontColor}
          domain={[props.graphDispSetting.xMin, props.graphDispSetting.xMax]}
          height={50}
          ticks={GetMemoryScaleByNumber(props.graphDispSetting.xMin, props.graphDispSetting.xMax, 10)}
          tick={{ dy: 10 }}
          padding={{ right: 0, left: 0 }}
        >
          <Label angle={0} position="insideBottomRight" style={{ textAnchor: 'middle', padding: 0 }}>
            msec
          </Label>
        </XAxis>
        <YAxis
          yAxisId={1}
          allowDataOverflow={true}
          dataKey="yValue"
          type="number"
          stroke={graphSetting.memoryFontColor}
          tickFormatter={(props) => props.toFixed(2)}
          ticks={getTicks(graphSetting.y1Min, graphSetting.y1Max)}
          domain={[graphSetting.y1Min, graphSetting.y1Max]}
          width={95}
        >
          <Label angle={270} position="insideLeft" style={{ textAnchor: 'middle', padding: 0 }}>
            {graphSetting.yUnit1}
          </Label>
        </YAxis>
        <YAxis
          yAxisId={2}
          orientation="right"
          dataKey="yValue"
          type="number"
          allowDataOverflow={true}
          stroke={graphSetting.memoryFontColor}
          tickFormatter={(props) => props.toFixed(2)}
          ticks={getTicks(graphSetting.y2Min, graphSetting.y2Max)}
          domain={[graphSetting.y2Min, graphSetting.y2Max]}
          width={95}
        >
          <Label angle={270} position="insideRight" style={{ textAnchor: 'middle', padding: 0 }}>
            {graphSetting.yUnit2}
          </Label>
        </YAxis>
        <Tooltip content={<CustomTooltip />} />
        <Line
          yAxisId={1}
          connectNulls
          dataKey="yValue"
          stroke={graphData[0]?.infoColor}
          data={graphData[0]?.points}
          strokeWidth={2}
          dot={false}
          name={graphData[0]?.lineName}
          hide={!isLineShow(0)}
          isAnimationActive={false}
        />
        <Line
          yAxisId={2}
          connectNulls
          dataKey="yValue"
          stroke={graphData[1]?.infoColor}
          data={graphData[1]?.points}
          strokeWidth={2}
          dot={false}
          name={graphData[1]?.lineName}
          hide={!isLineShow(1)}
          isAnimationActive={false}
        />
        {/* {graphData?.map((row, index) => (
          <Line
            yAxisId={1}
            connectNulls
            dataKey="yValue"
            stroke={row.infoColor}
            data={row.points}
            strokeWidth={2}
            dot={false}
            name={row.lineName}
            hide={!isLineShow(index)}
          />
        ))} */}
      </LineChart>
    </ResponsiveContainer>
  );
};
export default RawWaveformComparisonChart;
